export var PaymentPlanFrequency;
(function (PaymentPlanFrequency) {
    PaymentPlanFrequency["WEEKLY"] = "weekly";
    PaymentPlanFrequency["FORTNIGHTLY"] = "fortnightly";
    PaymentPlanFrequency["MONTHLY"] = "monthly";
    PaymentPlanFrequency["FOURWEEKLY"] = "fourweekly";
    PaymentPlanFrequency["BIMONTHLY"] = "bimonthly";
    PaymentPlanFrequency["QUARTERLY"] = "quarterly";
})(PaymentPlanFrequency || (PaymentPlanFrequency = {}));
export var PaymentPlanFirstPaymentDate;
(function (PaymentPlanFirstPaymentDate) {
    PaymentPlanFirstPaymentDate["Now"] = "now";
    PaymentPlanFirstPaymentDate["DueDate"] = "dueDate";
    PaymentPlanFirstPaymentDate["NowPlusFrequency"] = "nowPlusFrequency";
})(PaymentPlanFirstPaymentDate || (PaymentPlanFirstPaymentDate = {}));
export var PaymentPlanSurchargeUnit;
(function (PaymentPlanSurchargeUnit) {
    PaymentPlanSurchargeUnit["Percent"] = "percent";
    PaymentPlanSurchargeUnit["FixedAmountCents"] = "fixedAmountCents";
})(PaymentPlanSurchargeUnit || (PaymentPlanSurchargeUnit = {}));
export var PaymentPlanBrandingThemeFilterType;
(function (PaymentPlanBrandingThemeFilterType) {
    PaymentPlanBrandingThemeFilterType["Include"] = "include";
    PaymentPlanBrandingThemeFilterType["Exclude"] = "exclude";
})(PaymentPlanBrandingThemeFilterType || (PaymentPlanBrandingThemeFilterType = {}));
export const defaultPaymentPlanConfigValues = {
    maxNumberOfPayments: 12,
    frequencyType: PaymentPlanFrequency.MONTHLY,
    firstPaymentDates: [
        PaymentPlanFirstPaymentDate.Now,
        PaymentPlanFirstPaymentDate.DueDate,
        PaymentPlanFirstPaymentDate.NowPlusFrequency,
    ],
    surchargeCents: null,
    surchargePercent: null,
    surchargeUnit: null,
    externalSurchargeAccountCode: null,
    minInvoiceDaysOverdue: null,
    maxInvoiceDaysOverdue: null,
    brandingThemeFilterType: null,
    brandingThemeIds: null,
};
export const paymentPlanUtils = {
    getFrequencyTypeInterval,
    termsAreValid,
    createDefaultTermsForConfig,
    instalmentsMatchTotal,
};
function getFrequencyTypeInterval(frequencyType) {
    switch (frequencyType) {
        case PaymentPlanFrequency.WEEKLY:
            return { frequency: 1, frequencyUnit: `weeks` };
        case PaymentPlanFrequency.FORTNIGHTLY:
            return { frequency: 2, frequencyUnit: `weeks` };
        case PaymentPlanFrequency.FOURWEEKLY:
            return { frequency: 4, frequencyUnit: `weeks` };
        case PaymentPlanFrequency.BIMONTHLY:
            return { frequency: 2, frequencyUnit: `months` };
        case PaymentPlanFrequency.QUARTERLY:
            return { frequency: 3, frequencyUnit: `months` };
        case PaymentPlanFrequency.MONTHLY:
        default:
            return { frequency: 1, frequencyUnit: `months` };
    }
}
function instalmentsMatchTotal(instalments, totalCents) {
    const sumCents = instalments.reduce((sum, inst) => sum + inst.amountCents, 0);
    if (sumCents !== totalCents) {
        return { valid: false, reason: `installment amounts (${sumCents} cents) do not match total (${totalCents} cents)` };
    }
    return { valid: true };
}
const MAX_TERM_YEARS = 6;
function termsAreValid(terms, config) {
    const { numberOfPayments, frequencyType, firstPaymentDate, surchargeCents, surchargePercent, surchargeUnit, externalSurchargeAccountCode, } = terms;
    // Basic sanity checks
    // Enforce at least 2 payments so that a "payment plan" always splits the invoice
    if (!Number.isInteger(numberOfPayments) || numberOfPayments < 2) {
        return { valid: false, reason: `number of payments must be an integer greater than or equal to 2` };
    }
    if (!Object.values(PaymentPlanFrequency).includes(frequencyType)) {
        return { valid: false, reason: `frequency type is invalid` };
    }
    const interval = getFrequencyTypeInterval(frequencyType);
    if (surchargeUnit) {
        if (surchargeUnit === PaymentPlanSurchargeUnit.FixedAmountCents && !surchargeCents) {
            return { valid: false, reason: `surcharge value must be provided when surcharge is fixed amount` };
        }
        if (surchargeUnit === PaymentPlanSurchargeUnit.Percent && !surchargePercent) {
            return { valid: false, reason: `surcharge percent value must be provided when surcharge is percent` };
        }
        if (!(externalSurchargeAccountCode === null || externalSurchargeAccountCode === void 0 ? void 0 : externalSurchargeAccountCode.trim())) {
            return { valid: false, reason: `surcharge account code must be provided when surcharge is applied` };
        }
    }
    // If no config provided, apply sensible defaults used by UI/business rules
    // - Minimum numberOfPayments defaults to 2
    // - Only enforce maximum term length (<= MAX_TERM_YEARS) by default
    if (!config) {
        if (numberOfPayments < 2) {
            return { valid: false, reason: `number of payments below minimum (2)` };
        }
        // Estimate term span in days across (n - 1) intervals
        const intervals = Math.max(0, numberOfPayments - 1);
        const unitToDays = {
            days: 1,
            weeks: 7,
            months: 30, // Approximation suitable for validation
        };
        const perIntervalDays = unitToDays[interval.frequencyUnit];
        const estimatedTermDays = intervals * interval.frequency * perIntervalDays;
        if (estimatedTermDays > (365 * MAX_TERM_YEARS)) {
            return { valid: false, reason: `term length above maximum (${MAX_TERM_YEARS} years)` };
        }
        return { valid: true };
    }
    // Config bounds (when config is provided)
    if (config.surchargeUnit) {
        if (surchargeUnit !== config.surchargeUnit) {
            return { valid: false, reason: `surcharge unit must be ${config.surchargeUnit}` };
        }
        // Values not being zero is checked above
        if (config.surchargeUnit === PaymentPlanSurchargeUnit.FixedAmountCents && surchargeCents !== config.surchargeCents) {
            return { valid: false, reason: `surcharge cents must be ${config.surchargeCents}` };
        }
        if (config.surchargeUnit === PaymentPlanSurchargeUnit.Percent && surchargePercent !== config.surchargePercent) {
            return { valid: false, reason: `surcharge percent must be ${config.surchargePercent}` };
        }
        if (externalSurchargeAccountCode !== config.externalSurchargeAccountCode) {
            return { valid: false, reason: `surcharge account code must be ${config.externalSurchargeAccountCode}` };
        }
    }
    if (numberOfPayments > config.maxNumberOfPayments) {
        return { valid: false, reason: `number of payments above maximum (${config.maxNumberOfPayments})` };
    }
    if (frequencyType !== config.frequencyType) {
        return {
            valid: false,
            reason: `frequency type must be ${config.frequencyType}`,
        };
    }
    if (!config.firstPaymentDates.includes(firstPaymentDate)) {
        return { valid: false, reason: `first payment date not allowed` };
    }
    // Term length validation (span from first to last payment)
    // Span is (numberOfPayments - 1) intervals.
    const intervals = Math.max(0, numberOfPayments - 1);
    const unitToDays = {
        days: 1,
        weeks: 7,
        months: 30, // Approximation suitable for config-level validation
    };
    const perIntervalDays = unitToDays[interval.frequencyUnit];
    const estimatedTermDays = intervals * interval.frequency * perIntervalDays;
    if (estimatedTermDays > (365 * MAX_TERM_YEARS)) {
        return { valid: false, reason: `term length above maximum (${MAX_TERM_YEARS} years)` };
    }
    return { valid: true };
}
function createDefaultTermsForConfig(config) {
    if (!config) {
        return {
            numberOfPayments: 2,
            frequencyType: PaymentPlanFrequency.MONTHLY,
            firstPaymentDate: PaymentPlanFirstPaymentDate.Now,
            surchargeCents: 100,
            surchargePercent: 10,
            surchargeUnit: null,
            externalSurchargeAccountCode: null,
        };
    }
    return {
        numberOfPayments: Math.min(2, config.maxNumberOfPayments),
        frequencyType: config.frequencyType,
        firstPaymentDate: config.firstPaymentDates[0],
        surchargeCents: config.surchargeCents || null,
        surchargePercent: config.surchargePercent || null,
        surchargeUnit: config.surchargeUnit || null,
        externalSurchargeAccountCode: config.externalSurchargeAccountCode || null,
    };
}
export const paymentPlanConfigFields = {
    [`firstPaymentDates`]: { type: `base` },
    [`maxNumberOfPayments`]: { type: `base` },
    [`surchargeCents`]: { type: `base` },
    [`surchargePercent`]: { type: `base` },
    [`surchargeUnit`]: { type: `base` },
    [`externalSurchargeAccountCode`]: { type: `base` },
    [`minInvoiceDaysOverdue`]: { type: `filter` },
    [`maxInvoiceDaysOverdue`]: { type: `filter` },
    [`brandingThemeFilterType`]: { type: `filter` },
    [`brandingThemeIds`]: { type: `filter` },
    [`frequencyType`]: { type: `base` },
};
//# sourceMappingURL=paymentPlan.js.map